import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { signupSchema, SignupFormData } from '../schemas/validation';
import { AuthAPI } from '../services/api';
import { AppHeader } from './AppHeader';
import { PasswordField } from './PasswordField';
import { LoadingScreen } from './LoadingScreen';
import { AlertService } from '../services/AlertService';
import './AuthForms.css';

interface SignupFormProps {
  onSwitchToLogin?: () => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onSwitchToLogin }) => {
  const { signup, loading, logout } = useAuth();
  const [error, setError] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [loadingToken, setLoadingToken] = useState<boolean>(false);
  const alertService = AlertService;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const inviteToken = watch('invite_token');

  // Fetch email from invite token
  useEffect(() => {
    const fetchEmailFromToken = async () => {
      if (inviteToken && inviteToken.length > 10) {
        setLoadingToken(true);
        try {
          console.log('SignupForm: Validating invite token:', inviteToken);
          const inviteInfo = await AuthAPI.validateInviteToken(inviteToken);
          console.log('SignupForm: Invite info received:', inviteInfo);
          
          setInviteEmail(inviteInfo.email);
          setValue('email', inviteInfo.email);
          setError('');
          
          console.log('SignupForm: Email set to:', inviteInfo.email);
        } catch (err: any) {
          console.log('SignupForm: Token validation error:', err);
          setInviteEmail('');
          setValue('email', ''); // Clear email if invalid token
          setError('Invalid invite token');
        } finally {
          setLoadingToken(false);
        }
      } else {
        setInviteEmail('');
        setValue('email', ''); // Clear email if no valid token
        setLoadingToken(false);
      }
    };

    fetchEmailFromToken();
  }, [inviteToken, setValue]);

  const onSubmit = async (data: SignupFormData) => {
    try {
      setError('');
      
      // Logout any existing user first
      await logout();
      
      // Debug: Log the current state and form data
      console.log('SignupForm: Current invite email state:', inviteEmail);
      console.log('SignupForm: Form data being submitted:', data);
      console.log('SignupForm: JSON stringified data:', JSON.stringify(data, null, 2));
      
      // Ensure email is included
      if (!data.email) {
        setError('Email is required. Please enter a valid invite token.');
        return;
      }
      
      // Remove confirmPassword from the signup data
      const { confirmPassword, ...signupData } = data;
      
      await signup(signupData);
      alertService.success('Account created successfully! You can now log in.', 2000);
      reset();
      
      // Switch to login form after a short delay
      if (onSwitchToLogin) {
        setTimeout(() => {
          onSwitchToLogin();
        }, 2000);
      }
    } catch (err: any) {
      console.log('SignupForm: Signup error:', err);
      setError(err.message || 'Signup failed');
    }
  };

  return (
    <div className="auth-page">
      <AppHeader showUserInfo={false} />
      <div className="auth-form-container">
        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <h2>Sign Up</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-group">
          <label htmlFor="invite_token">Invite Token</label>
          <input
            id="invite_token"
            type="text"
            {...register('invite_token')}
            className={errors.invite_token ? 'error' : ''}
            placeholder="Enter your invite token"
            disabled={loading}
          />
          {errors.invite_token && <span className="field-error">{errors.invite_token.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="full_name">Full Name</label>
          <input
            id="full_name"
            type="text"
            {...register('full_name')}
            className={errors.full_name ? 'error' : ''}
            placeholder="Enter your full name"
            disabled={loading}
          />
          {errors.full_name && <span className="field-error">{errors.full_name.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">
            Email (from invite token)
            {loadingToken && <span style={{ color: 'var(--primary)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>Loading...</span>}
            {inviteEmail && <span style={{ color: 'var(--success)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>✓ Loaded</span>}
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={`${errors.email ? 'error' : ''} ${inviteEmail ? 'readonly' : ''}`}
            placeholder={
              loadingToken ? 'Loading email from token...' :
              inviteToken && !inviteEmail ? 'Enter valid invite token to see email' :
              inviteEmail ? inviteEmail :
              'your.name@erpamperedeals.com'
            }
            readOnly
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: inviteEmail ? 'var(--text-primary)' : 'var(--text-secondary)',
              cursor: 'not-allowed'
            }}
          />
          {errors.email && <span className="field-error">{errors.email.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            {...register('username')}
            className={errors.username ? 'error' : ''}
            placeholder="Choose a username"
            disabled={loading}
          />
          {errors.username && <span className="field-error">{errors.username.message}</span>}
        </div>

        <PasswordField
          id="password"
          label="Password"
          placeholder="Create a strong password"
          register={register}
          error={errors.password?.message}
          disabled={loading || loadingToken}
          showRequirements={true}
        />

        <PasswordField
          id="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm your password"
          register={register}
          error={errors.confirmPassword?.message}
          disabled={loading || loadingToken}
        />

        <button 
          type="submit" 
          disabled={loading || loadingToken}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          {loading ? 'Creating Account...' : loadingToken ? 'Loading Token...' : 'Sign Up'}
        </button>

        {onSwitchToLogin && (
          <p className="switch-form">
            Already have an account? <button type="button" onClick={onSwitchToLogin} className="btn btn-ghost">Login</button>
          </p>
        )}
        </form>
      </div>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading || loadingToken} message={loadingToken ? "Validating invitation..." : "Creating your account..."} />
    </div>
  );
};/* Updated */
